import {
  CheckCircle2,
  AlertTriangle,
  MinusCircle,
  Ban,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CosmoQuestion } from "@/types/scorecard";

export interface StatusConfigEntry {
  icon: LucideIcon;
  label: string;
  color: string;
  bg: string;
  badgeClass: string;
}

export const statusConfig: Record<CosmoQuestion["status"], StatusConfigEntry> = {
  strong: { icon: CheckCircle2, label: "Strong", color: "text-status-success-strong", bg: "bg-emerald-50 border-emerald-200", badgeClass: "bg-status-success-soft border-0 text-foreground" },
  moderate: { icon: AlertTriangle, label: "Opportunity", color: "text-amber-600", bg: "bg-amber-50 border-amber-200", badgeClass: "bg-status-warning-soft border-0 text-foreground" },
  // AUT-355: --status-weak-bg was removed (the mockup has no "weak" tier). "Potential Gap" is a
  // neutral, non-alarming verdict -- not an error/warning -- so it reads best as a muted surface
  // tint rather than being pulled into the status palette at all. surface-tint-medium keeps the
  // pastel weight the old token had while staying visually distinct from `not_applicable`'s plain
  // `bg-slate-100`.
  weak: { icon: MinusCircle, label: "Potential Gap", color: "text-orange-600", bg: "bg-orange-50 border-orange-200", badgeClass: "bg-surface-tint-medium border-0 text-foreground" },
  missing: { icon: MinusCircle, label: "Potential Gap", color: "text-orange-600", bg: "bg-red-50 border-red-200", badgeClass: "bg-surface-tint-medium border-0 text-foreground" },
  // AUT-441: the badge reads `Not applicable`, not `N/A`. The prototype had no `not_applicable`
  // key at all when `design-input/amazon-ai-score-tab/` was captured; it gained one on 2026-09-24
  // and QA filed AUT-441 against the difference the same day. Label only -- icon and palette stay
  // ours as a blast-radius call, and the design's own values for them are recorded in
  // `sources.md § Re-verified 2026-09-29`. `App\Enums\ScorecardVerdict::label()` still says
  // `N/A`; the two owners disagree on this one word on purpose. Note this label is NOT
  // screen-only -- `useVariantBActions.ts:351` puts it in a prompt payload.
  // Full reasoning: `components/scorecard.md § The verdict vocabulary`.
  not_applicable: { icon: Ban, label: "Not applicable", color: "text-slate-400", bg: "bg-slate-50 border-slate-200", badgeClass: "bg-slate-100 border-slate-200 text-muted-foreground" },
};

/**
 * Every SPA site that shows a verdict reads this instead of indexing `statusConfig` directly, so a
 * value the map does not know renders `N/A` in one place rather than crashing or leaking at each
 * call site (`constraints.md § C-34`).
 */
export function getStatusConfig(status: string): StatusConfigEntry {
  return statusConfig[status as CosmoQuestion["status"]] ?? statusConfig.not_applicable;
}
